/**
 * HistoryModal — Timeline & History modal following Settings Modal Pattern.
 *
 * Shows document activity (comments, edits, assignments, attachments) in
 * an iOS-style inset container with proper fade gradients, scrollable
 * content, and a sticky Done footer.
 *
 * Data: useDocChat (infinite query, oldest → newest).
 * Presentational only — no mutations.
 */

import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  MessageCircle,
  Paperclip,
  UserPlus,
  Share2,
  Heart,
  FileText,
  Clock,
} from "lucide-react";
import { useDocChat } from "@/features/common/api/useDocChat";
import type { DocChatItem } from "@/lib/erp/docs";
import type { LucideIcon } from "lucide-react";

interface HistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  doctype: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getActivityIcon(item: DocChatItem): LucideIcon {
  const eventType = (item.meta?.event_type || item.type || "").toLowerCase();
  if (eventType.includes("comment")) return MessageCircle;
  if (eventType.includes("attachment") || eventType.includes("file")) return Paperclip;
  if (eventType.includes("assigned") || eventType.includes("assignment")) return UserPlus;
  if (eventType.includes("shared") || eventType.includes("unshared")) return Share2;
  if (eventType.includes("like")) return Heart;
  if (eventType.includes("version") || eventType.includes("changed") || eventType.includes("field")) return FileText;
  return Clock;
}

function formatRelativeTime(creation: string): string {
  try {
    const d = new Date(creation);
    if (Number.isNaN(d.getTime())) return creation;

    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60_000);
    const diffHr = Math.floor(diffMs / 3_600_000);
    const diffDay = Math.floor(diffMs / 86_400_000);

    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;

    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return creation;
  }
}

function renderContent(item: DocChatItem): React.ReactNode {
  if (item.content) {
    return <span dangerouslySetInnerHTML={{ __html: item.content }} />;
  }
  if (item.plain_text) return <span>{item.plain_text}</span>;

  const eventType = (item.meta?.event_type || item.type || "").toLowerCase();
  if (eventType.includes("assignment")) return <span>Assignment updated</span>;
  if (eventType.includes("attachment")) return <span>File updated</span>;
  if (eventType.includes("version")) return <span>Document updated</span>;
  return <span>Activity</span>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function HistoryModal({ open, onOpenChange, doctype, name }: HistoryModalProps) {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useDocChat(doctype, name);

  const items = data?.items ?? [];
  // Show newest first in the modal (reverse chronological)
  const reversed = useMemo(() => [...items].reverse(), [items]);
  const hasMore = data?.hasMore ?? false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-[680px] p-0 flex flex-col overflow-hidden">
        {/* 1. Top fade gradient */}
        <div className="absolute top-0 left-0 right-0 z-30 h-12 bg-gradient-to-b from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />

        {/* 2. Scrollable content */}
        <div className="flex-1 overflow-y-auto relative will-change-[scroll-position]">
          {/* 3. Header */}
          <DialogHeader className="px-10 pt-20 pb-4 !flex-row flex items-center justify-between space-y-0">
            <div>
              <DialogTitle className="text-[22px] font-semibold text-foreground leading-tight tracking-[-0.2px]">
                Timeline & History
              </DialogTitle>
              {!isLoading && (
                <p className="text-sm text-muted-foreground mt-1">
                  {reversed.length} {reversed.length === 1 ? "activity" : "activities"}
                </p>
              )}
            </div>
          </DialogHeader>

          {/* 4. Content area */}
          <div className="px-10 pb-8 pt-2">
            {isLoading ? (
              <div className="flex flex-col items-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/60" />
              </div>
            ) : reversed.length === 0 ? (
              /* Empty state */
              <div className="flex flex-col items-center py-10">
                <Clock className="h-12 w-12 text-muted-foreground/40 mb-3" />
                <p className="text-base font-medium text-foreground">No activity yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Changes and comments will appear here
                </p>
              </div>
            ) : (
              /* Inset container with activity rows */
              <div className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
                {reversed.map((item, i) => {
                  const Icon = getActivityIcon(item);
                  const actor = item.user_full_name || item.owner;
                  const time = formatRelativeTime(item.creation);

                  return (
                    <div key={item.name}>
                      <div className="flex items-start gap-3 px-5 py-4">
                        {/* Icon thumbnail — 40x40 */}
                        <div className="flex items-center justify-center h-10 w-10 rounded-md bg-muted/50 shrink-0 mt-0.5">
                          <Icon className="h-5 w-5 text-muted-foreground/70" />
                        </div>
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="text-[17px] text-gray-900 dark:text-gray-100 leading-snug line-clamp-3 [&_a]:text-[#007AFF] [&_a]:no-underline">
                            {renderContent(item)}
                          </div>
                          <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground/70">
                            <span className="font-medium truncate max-w-[140px]">{actor}</span>
                            <span>{"\u00B7"}</span>
                            <span className="shrink-0">{time}</span>
                          </div>
                        </div>
                      </div>
                      {/* Divider — indented */}
                      {i < reversed.length - 1 && (
                        <div className="mx-5 border-t border-gray-200/40 dark:border-gray-700/40" />
                      )}
                    </div>
                  );
                })}

                {/* Load more — inside container */}
                {hasMore && hasNextPage && (
                  <>
                    <div className="mx-5 border-t border-gray-200/40 dark:border-gray-700/40" />
                    <div className="flex justify-center px-5 py-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => fetchNextPage()}
                        disabled={isFetchingNextPage}
                        className="h-9 px-4 text-sm text-[#007AFF] hover:text-[#0051D5] active:text-[#0040B3] hover:bg-transparent"
                      >
                        {isFetchingNextPage ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          "Load older activity"
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* 5. Bottom fade gradient */}
          <div className="sticky bottom-0 left-0 right-0 z-10 h-24 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
        </div>

        {/* 6. Sticky footer — OUTSIDE scroll container */}
        <div className="flex gap-3 px-10 py-6 bg-white dark:bg-gray-900 border-t border-gray-200/50 dark:border-gray-800/50 flex-shrink-0">
          <Button
            onClick={() => onOpenChange(false)}
            className="flex-1 rounded-full h-11 bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] text-white text-[17px] font-semibold"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
